import { Fragment, useState, useEffect } from "react";

// next.js components
import Image from "next/image";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";

// next-mdx-remote components
import { MDXRemote } from "next-mdx-remote";
import { serialize } from "next-mdx-remote/serialize";

import dynamic from "next/dynamic";
// mdx-observable components
import { State, Observe } from "mdx-observable";

// markdown utils
import fs from "fs";
import matter from "gray-matter";
import rehypePrettyCode from "rehype-pretty-code";
import remarkGfm from "remark-gfm";

// page components
import DocsFooter from "components/layout/docs-footer";
import DocsNavbar from "components/layout/docs-navbar";
import Sidenav from "components/layout/sidenav";
import PageMap from "components/layout/page-map";
import ComponentDemo from "components/cards/component-demo";
import Code from "components/code";
// import Pre from "components/pre";
import CodeSandbox from "components/code-sandbox";
import StackBlitz from "components/stack-blitz";
import Framework from "components/cards/framework";
import CraLogo from "components/icons/cra";
import NextLogo from "components/icons/next";
import RemixLogo from "components/icons/remix";
import ViteLogo from "components/icons/vite";
import ColorPalette from "components/color-palette";
// import DocsRelated from "components/layout/docs-related";
import CodeTabs from "components/code-tabs";
import CodePreview from "components/code-preview";

// Custom Horizon components
import CardHorizon, {
  NFTCard,
  Storage,
  TaskCard
} from "../../packages/material-tailwind-react/src/components/CardHorizon";
import Widget from "../../packages/material-tailwind-react/src/components/CardHorizon/Widget";
import Dropdown from "../../packages/material-tailwind-react/src/components/Dropdown";
import ModalExample from "../../packages/material-tailwind-react/src/components/Modal";
import AccordionExample from "../../packages/material-tailwind-react/src/components/AccordionExample";
import CheckboxHorizon from "../../packages/material-tailwind-react/src/components/CheckboxHorizon";
import SwitchHorizon from "../../packages/material-tailwind-react/src/components/SwitchHorizon";
import PopoverHorizon from "../../packages/material-tailwind-react/src/components/PopoverHorizon";
import ProgressHorizon from "../../packages/material-tailwind-react/src/components/ProgressHorizon";
import RadioHorizon from "../../packages/material-tailwind-react/src/components/RadioHorizon";
import TooltipHorizon from "../../packages/material-tailwind-react/src/components/TooltipHorizon";
import ActionButtons from "../../packages/material-tailwind-react/src/components/Actions/ActionButtons";
import Follow from "../../packages/material-tailwind-react/src/components/Actions/Follow";
import SeeStory from "../../packages/material-tailwind-react/src/components/Actions/SeeStory";
import SetUp from "../../packages/material-tailwind-react/src/components/Actions/SetUp";
import EventCalendar from "../../packages/material-tailwind-react/src/components/Calendar/EventCalendar";
import MiniCalendar from "../../packages/material-tailwind-react/src/components/Calendar/MiniCalendar";
import BarChart from "../../packages/material-tailwind-react/src/components/Charts/BarChart";
import LineAreaChart from "../../packages/material-tailwind-react/src/components/Charts/LineAreaChart";
const LineChart = dynamic(
  () =>
    import(
      "../../packages/material-tailwind-react/src/components/Charts/LineChart"
    ),
  { ssr: false }
);
import PieChart from "../../packages/material-tailwind-react/src/components/Charts/PieChart";
import CircularProgress from "../../packages/material-tailwind-react/src/components/Charts/CircularProgress";
import Event from "../../packages/material-tailwind-react/src/components/DataDisplay/Event";
import OrderStep from "../../packages/material-tailwind-react/src/components/DataDisplay/OrderStep";
import SessionBadge from "../../packages/material-tailwind-react/src/components/DataDisplay/SessionBadge";
import TimelineItem from "../../packages/material-tailwind-react/src/components/DataDisplay/TimelineItem";
import Transaction from "../../packages/material-tailwind-react/src/components/DataDisplay/Transaction";
import Transfer from "../../packages/material-tailwind-react/src/components/DataDisplay/Transfer";
import DataTable from "../../packages/material-tailwind-react/src/components/DataTable";
import PinInput from "../../packages/material-tailwind-react/src/components/PinInput";
import Map from "../../packages/material-tailwind-react/src/components/Map";
import RangeSlider from "../../packages/material-tailwind-react/src/components/RangeSlider";
import Dropzone from "../../packages/material-tailwind-react/src/components/Dropzone";
import InputField from "../../packages/material-tailwind-react/src/components/Fields/InputField";
import TextField from "../../packages/material-tailwind-react/src/components/Fields/TextField";
import {
  MdCheck,
  MdPointOfSale,
  MdLocalBar,MdBarChart,MdDashboard
} from "../../packages/material-tailwind-react/node_modules/react-icons/md";
import {IoMdHome
} from "../../packages/material-tailwind-react/node_modules/react-icons/io";
// Deployment #1

// @material-tailwind/react components
import {
  Accordion,
  AccordionHeader,
  AccordionBody,
  Alert,
  Avatar,
  Breadcrumbs,
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Checkbox,
  Chip,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  IconButton,
  Input,
  Menu,
  MenuHandler,
  MenuList,
  MenuItem,
  MobileNav,
  Navbar,
  Option,
  Popover,
  PopoverHandler,
  PopoverContent,
  Progress,
  Radio,
  Select,
  Switch,
  Tabs,
  TabsHeader,
  TabsBody,
  Tab,
  TabPanel,
  Textarea,
  Tooltip,
  Typography,
} from "@material-tailwind/react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Accordion as AccordionChakra,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "../../packages/material-tailwind-react/node_modules/@chakra-ui/react";

// routes
// import { routes as htmlRoutes } from "routes/html.routes";
import { routes as reactRoutes } from "routes/react.routes";

// utils
import config from "utils/rehype-pretty-code-config";
import filterArray from "utils/filter-array";
import getDirectoriesAndFile from "utils/get-directories-and-files";

// Horizon UI html script
// import initHtmlScripts from "public/material-tailwind-html-v2";

const components = {
  h1: (props) => (
    <Typography
      as="h1"
      variant="h3"
      color="navy"
      className="!mb-4 lg:!text-4xl"
      {...props}
    />
  ),
  h2: (props) => (
    <Typography
      as="h2"
      variant="h4"
      color="navy"
      className="!mb-2"
      {...props}
    />
  ),
  h3: (props) => (
    <Typography
      as="h3"
      variant="h5"
      color="navy"
      className="!mb-2"
      {...props}
    />
  ),
  h6: (props) => (
    <Typography
      as="p"
      variant="h5"
      className="!mb-12 !font-normal !text-navy-700"
      {...props}
    />
  ),
  p: (props) => (
    <Typography className="!mb-4 !font-normal !text-navy-700" {...props} />
  ),
  hr: () => <hr className="border-transparent !mt-16 !mb-12" />,
  a: (props) => (
    <a
      target="_blank"
      className="!font-bold !text-brand-500 underline !transition-colors hover:!text-brand-600"
      {...props}
    />
  ),
  table: (props) => (
    <div className="!overflow-scroll">
      <table className="!w-full !min-w-[700px] lg:!min-w-full" {...props} />
    </div>
  ),
  thead: (props) => <thead {...props} />,
  tbody: (props) => <tbody {...props} />,
  tr: (props) => <tr {...props} />,
  th: (props) => (
    <th
      className="min-w-[120px] border-b border-navy-100 py-3 text-left font-medium text-navy-900"
      {...props}
    />
  ),
  td: (props) => (
    <td
      className="min-w-[120px] border-b border-gray-300 py-3 text-left text-sm text-navy-700"
      {...props}
    />
  ),
  State,
  Observe,
  CodeTabs,
  Image,
  ComponentDemo,
  CodeSandbox,
  StackBlitz,
  Code,
  Framework,
  CraLogo,
  NextLogo,
  RemixLogo,
  ViteLogo,
  ColorPalette,
  CodePreview,
  AccordionExample,
  Accordion,
  AccordionHeader,
  AccordionBody,
  Alert,
  Avatar,
  Breadcrumbs,
  Button,
  Card,
  NFTCard,
  TaskCard,
  CardHorizon,
  CardHeader,
  CardBody,
  CardFooter,
  Checkbox,
  CheckboxHorizon,
  Chip,Storage,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  IconButton,
  Input,
  Menu,
  MenuHandler,
  MenuList,
  MenuItem,
  MobileNav,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Navbar,
  Option,
  Popover,
  PopoverHandler,
  PopoverContent,
  PopoverHorizon,
  Progress,
  ProgressHorizon,
  RadioHorizon,
  Radio,
  Select,
  Switch,
  SwitchHorizon,
  Tabs,
  TabsHeader,
  TabsBody,
  Tab,
  TabPanel,
  Textarea,
  Tooltip,
  TooltipHorizon,
  Typography,
  Link,
  ModalExample,
  AccordionChakra,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  ActionButtons,
  Follow,
  SeeStory,
  SetUp,
  EventCalendar,
  MiniCalendar,
  BarChart,
  LineAreaChart,
  LineChart,
  PieChart,
  CircularProgress,
  Event,
  OrderStep,
  SessionBadge,
  TimelineItem,
  Transaction,
  Transfer,
  MdCheck,
  MdPointOfSale,
  MdLocalBar,
  Dropdown,
  DataTable,
  PinInput,
  Map,
  RangeSlider,
  Dropzone,
  InputField,
  TextField,
  Widget,
  MdBarChart,
  IoMdHome,
  MdDashboard
};

export default function Page({ frontMatter, mdxSource, slug }) {
  const { asPath } = useRouter();
  const [mobileNav, setMobileNav] = useState(false);

  const routes = {
    // html: htmlRoutes,
    react: reactRoutes,
  };
  const validFrameworks = ["html", "react", "vue", "angular", "svelte"];
  const frameworkType = asPath
    .split("/")
    .filter((el) => validFrameworks.includes(el))
    .join("") as "html" | "react";

  // useEffect(() => {
  //   if (frameworkType === "html" && typeof window !== "undefined") {
  //     initHtmlScripts();
  //   }
  // }, [frameworkType, slug]);

  return (
    <Fragment>
      <Head>
        <meta name="twitter:card" content="product" />
        <meta name="twitter:site" content="@horizon-ui" />
        <meta
          name="twitter:title"
          content="Horizon UI - Trendiest Free Admin Dashboard Template and Components Library for Tailwind CSS React"
        />

        <meta
          name="twitter:description"
          content="Horizon UI - Trendiest Free Admin Dashboard Template and Components Library for Tailwind CSS React. Horizon UI comes with over 70+ dark/light frontend individual elements, like buttons, inputs, navbars, nav tabs, cards, or alerts, giving you the freedom of choosing and combining. "
        />
        <meta name="twitter:creator" content="@horizon-ui" />
        <meta
          name="twitter:image"
          content="https://i.ibb.co/1zhBQ2J/horizon-ui-tailwind-2.png"
        />

        {/* Open Graph data */}
        <meta
          property="og:title"
          content="Horizon UI - Trendiest Free Admin Dashboard Template and Components Library for Tailwind CSS React"
        />
        <meta property="og:type" content="article" />
        <meta
          property="og:url"
          content="https://horizon-ui.com/horizon-tailwind-react/"
        />
        <meta
          property="og:image"
          content="https://i.ibb.co/1zhBQ2J/horizon-ui-tailwind-2.png"
        />
        <meta
          property="og:description"
          content="Horizon UI - Trendiest Free Admin Dashboard Template and Components Library for Tailwind CSS React. Horizon UI comes with over 70+ dark/light frontend individual elements, like buttons, inputs, navbars, nav tabs, cards, or alerts, giving you the freedom of choosing and combining. "
        />
        <title>{frontMatter.title}</title>
        <meta name="description" content={frontMatter.description} />
        <meta
          name="keywords"
          content="tailwind css, react, react.js, tailwind template, tailwind, tailwind css theme, tailwindcss components, horizon ui, typescript, admin tailwind, free tailwind css, admin template tailwind css, tailwind css free admin dashboard, tailwind css admin dashboard, free tailwind css, admin template, free tailwind admin, tailwind panel, admin template tailwind css, best tailwind css admin, best tailwind admin template, open source, react dashboard, react admin, react tailwind css admin, react tailwind css dashboard, react panel, react tailwind css template, react admin template, react free, react premium, react free dashboard, react premium dashboard, dashboard, react free tailwind css dashboard, free admin template, premium admin template, react tailwind css, react tailwind css dashboard, react tailwind css free, react free premium, react tailwind css free dashboard, react free premium dashboard, tailwind admin dashboard, admin dashboard, admin template, free admin template, tailwind css free template"
        />
      </Head>
      <div className="relative h-full w-full bg-white">
        <DocsNavbar slug={slug[slug.length - 1]} setMobileNav={setMobileNav} />
        <div className="px-6">
          <div className="mx-auto flex max-w-[1440px]">
            <Sidenav
              routes={routes[frameworkType]}
              type={frameworkType}
              slug={slug[slug.length - 1]}
              mobileNav={mobileNav}
              setMobileNav={setMobileNav}
            />
            <div className="mt-36 w-full lg:mt-28 lg:w-[64%] lg:px-8">
              {/* <Link href="/docs/react/installation">
                <button className="mb-5 flex flex-row items-center rounded-full bg-brand-500 px-4 py-1 text-sm font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200">
                  <div className="mr-2 flex h-4 w-4 items-center justify-center rounded-full p-4">
                    <i className="fa fa-solid fa-circle-exclamation text-xs text-white" />
                  </div>
                  Works with Horizon UI Dashboard
                </button>
              </Link> */}
              <MDXRemote {...mdxSource} components={components} />
              {/* {frontMatter.related && (
                <DocsRelated routes={frontMatter.related} />
              )} */}
              <DocsFooter type={frameworkType} frontMatter={frontMatter} />
            </div>
            <PageMap frontMatter={frontMatter} />
          </div>
        </div>
      </div>
    </Fragment>
  );
}

export const getStaticPaths = async () => {
  const baseDirectory = "documentation";
  const paths = [];
  const allDir = getDirectoriesAndFile(baseDirectory);
  const filteredArray = filterArray(allDir);

  for (let i = 0; i < filteredArray.length - 1; i++) {
    const directories =
      filteredArray[i] !== null &&
      filteredArray[i].split("/").filter((dir) => dir !== baseDirectory);
    const files = filteredArray[i + 1].includes("/")
      ? filteredArray[i + 1].split("/").filter((dir) => dir !== baseDirectory)
      : filteredArray[i + 1];

    const filename = Array.isArray(files) ? null : files;

    filename &&
      paths.push({
        params: {
          slug: [...directories, filename],
        },
      });
  }

  return {
    paths: paths,
    fallback: false,
  };
};

export const getStaticProps = async ({ params: { slug } }) => {
  const markdownWithMeta = fs.readFileSync(
    `documentation/${slug.join("/")}.mdx`
  );

  const { data: frontMatter, content } = matter(markdownWithMeta);

  const mdxSource = await serialize(content, {
    mdxOptions: {
      rehypePlugins: [[rehypePrettyCode, config]],
      remarkPlugins: [remarkGfm],
      development: false,
    },
  });

  return {
    props: {
      frontMatter,
      mdxSource,
      slug,
    },
  };
};
